import React, { useState, useEffect } from 'react';
import {
  History,
  Plus,
  RotateCcw,
  Eye,
  ChevronDown,
  ChevronRight,
  Save,
  X,
  Loader2,
  GitCompare,
  FileCode,
} from 'lucide-react';
import { formatDateTimeShort } from '../helpers/formatDateUTC';
import {
  useCheckpoints,
  useCreateCheckpoint,
  useRestoreCheckpoint,
  useCheckpointPreview,
} from '../helpers/useCheckpointsApi';
import { getConnectedRepo, ConnectedRepoInfo } from './GithubImportTab';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Badge } from './Badge';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from './Dialog';
import { Skeleton } from './Skeleton';
import { CheckpointType } from '../helpers/schema';
import { FileDiffViewer } from './FileDiffViewer';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import { toast } from 'sonner';
import styles from './CheckpointsPanel.module.css';

interface CheckpointsPanelProps {
  sessionId: string;
  onPreviewCheckpoint?: (checkpointId: string) => void;
  currentFiles?: Selectable<SessionFiles>[];
  className?: string;
}

export const CheckpointsPanel: React.FC<CheckpointsPanelProps> = ({
  sessionId,
  onPreviewCheckpoint,
  currentFiles = [],
  className,
}) => {
  const { data, isLoading } = useCheckpoints(sessionId);
  const createMutation = useCreateCheckpoint();
  const restoreMutation = useRestoreCheckpoint();

  const [isCreating, setIsCreating] = useState(false);
  const [newCheckpointName, setNewCheckpointName] = useState('');
  const [newCheckpointDesc, setNewCheckpointDesc] = useState('');
  const [newCheckpointType, setNewCheckpointType] = useState<CheckpointType>('manual');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [diffCheckpointId, setDiffCheckpointId] = useState<string | null>(null);
  const [connectedRepo, setConnectedRepo] = useState<ConnectedRepoInfo | null>(null);

  // Load connected repo for auto-push
  useEffect(() => {
    const repo = getConnectedRepo(sessionId);
    setConnectedRepo(repo);
  }, [sessionId]);

  const { data: diffCheckpointData } = useCheckpointPreview(diffCheckpointId);

  const handleCreate = async () => {
    if (!newCheckpointName.trim()) return;

    await createMutation.mutateAsync({
      sessionId,
      name: newCheckpointName,
      description: newCheckpointDesc,
      checkpointType: newCheckpointType,
      // Auto-push to GitHub if connected
      githubRepo: connectedRepo?.repo,
      githubBranch: connectedRepo?.branch,
    });

    setIsCreating(false);
    setNewCheckpointName('');
    setNewCheckpointDesc('');
    setNewCheckpointType('manual');
  };

  const handleRestore = async () => {
    if (!restoreId) return;
    await restoreMutation.mutateAsync({ checkpointId: restoreId });
    setRestoreId(null);
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getBadgeVariant = (type: CheckpointType) => {
    switch (type) {
      case 'milestone':
        return 'success';
      case 'auto':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Helper to parse and count files in checkpoint snapshot
  const getCheckpointFiles = (filesSnapshot: any): { filename: string; path: string }[] => {
    try {
      const files = typeof filesSnapshot === 'string' ? JSON.parse(filesSnapshot) : filesSnapshot;
      if (!Array.isArray(files)) return [];
      return files.map((f: any) => ({
        filename: f.fileName || f.filename || 'unknown',
        path: f.filePath || f.path || '/',
      }));
    } catch {
      return [];
    }
  };

  return (
    <div className={`${styles.panel} ${className || ''}`}>
      <div className={styles.header}>
        <div className={styles.title}>
          <History size={16} className={styles.icon} />
          <span>Checkpoints</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsCreating(!isCreating)}
          disabled={isCreating}
        >
          <Plus size={14} /> New
        </Button>
      </div>

      {isCreating && (
        <div className={styles.createForm}>
          <Input
            placeholder="Checkpoint Name"
            value={newCheckpointName}
            onChange={(e) => setNewCheckpointName(e.target.value)}
            autoFocus
          />
          <Textarea
            placeholder="Description (optional)"
            value={newCheckpointDesc}
            onChange={(e) => setNewCheckpointDesc(e.target.value)}
            rows={2}
          />
          <div className={styles.formRow}>
            <Select
              value={newCheckpointType}
              onValueChange={(val) => setNewCheckpointType(val as CheckpointType)}
            >
              <SelectTrigger className={styles.selectTrigger}>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="milestone">Milestone</SelectItem>
              </SelectContent>
            </Select>
            <div className={styles.formActions}>
              <Button variant="ghost" size="sm" onClick={() => setIsCreating(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!newCheckpointName.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <Loader2 className={styles.spinner} size={14} />
                ) : (
                  <Save size={14} />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.list}>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.skeletonItem}>
              <Skeleton style={{ width: '100%', height: '3rem' }} />
            </div>
          ))
        ) : data?.checkpoints.length === 0 ? (
          <div className={styles.emptyState}>
            <History size={32} />
            <p>No checkpoints yet</p>
          </div>
        ) : (
          data?.checkpoints.map((cp) => {
            const checkpointFiles = getCheckpointFiles(cp.filesSnapshot);
            const fileCount = checkpointFiles.length;

            return (
              <div key={cp.id} className={styles.item}>
                <div className={styles.itemHeader} onClick={() => toggleExpand(cp.id)}>
                  <button className={styles.expandBtn}>
                    {expandedId === cp.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemNameRow}>
                      <span className={styles.itemName}>{cp.name || 'Unnamed checkpoint'}</span>
                      <Badge variant={getBadgeVariant(cp.checkpointType)} className={styles.badge}>
                        {cp.checkpointType || 'manual'}
                      </Badge>
                    </div>
                    <div className={styles.itemMeta}>
                      <span>{cp.createdAt ? formatDateTimeShort(cp.createdAt) : 'Unknown'}</span>
                      {fileCount > 0 && (
                        <span className={styles.fileCount}>
                          <FileCode size={12} />
                          {fileCount} file{fileCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {cp.createdBy && (
                        <div className={styles.userMeta}>
                          <Avatar className={styles.avatar}>
                            <AvatarImage src={cp.createdBy.avatarUrl || undefined} />
                            <AvatarFallback>{cp.createdBy.displayName?.[0] ?? '?'}</AvatarFallback>
                          </Avatar>
                          <span>{cp.createdBy.displayName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {expandedId === cp.id && (
                  <div className={styles.itemDetails}>
                    {cp.description && <p className={styles.description}>{cp.description}</p>}

                    {/* File list */}
                    {checkpointFiles.length > 0 && (
                      <div className={styles.filesList}>
                        <div className={styles.filesHeader}>
                          <FileCode size={12} />
                          <span>Files in this checkpoint:</span>
                        </div>
                        <div className={styles.filesGrid}>
                          {checkpointFiles.slice(0, 10).map((file, idx) => (
                            <span key={idx} className={styles.fileChip}>
                              {file.path !== '/' ? `${file.path}/` : ''}
                              {file.filename}
                            </span>
                          ))}
                          {checkpointFiles.length > 10 && (
                            <span className={styles.moreFiles}>
                              +{checkpointFiles.length - 10} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={styles.actions}>
                      {onPreviewCheckpoint && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onPreviewCheckpoint(cp.id)}
                          className={styles.actionBtn}
                        >
                          <Eye size={14} /> Preview
                        </Button>
                      )}
                      {currentFiles.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDiffCheckpointId(cp.id)}
                          className={styles.actionBtn}
                        >
                          <GitCompare size={14} /> View Diff
                        </Button>
                      )}

                      <Dialog
                        open={restoreId === cp.id}
                        onOpenChange={(open) => setRestoreId(open ? cp.id : null)}
                      >
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className={styles.actionBtn}>
                            <RotateCcw size={14} /> Restore
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Restore Checkpoint?</DialogTitle>
                            <DialogDescription>
                              This will revert all files to their state at &quot;{cp.name}&quot;.
                              Current unsaved changes will be lost. This action creates a new
                              auto-checkpoint before restoring.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="ghost" onClick={() => setRestoreId(null)}>
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={handleRestore}
                              disabled={restoreMutation.isPending}
                            >
                              {restoreMutation.isPending ? 'Restoring...' : 'Confirm Restore'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {diffCheckpointData?.checkpoint && (
        <FileDiffViewer
          open={!!diffCheckpointId}
          onOpenChange={(open) => setDiffCheckpointId(open ? diffCheckpointId : null)}
          currentFiles={currentFiles}
          checkpointFiles={
            Array.isArray(diffCheckpointData.checkpoint.filesSnapshot)
              ? diffCheckpointData.checkpoint.filesSnapshot
              : []
          }
          checkpointName={diffCheckpointData.checkpoint.name}
        />
      )}
    </div>
  );
};
